import Vue from 'vue';
import Component from 'vue-class-component';
import KEYS from '@/values/keys';
import { Prop, Ref, Watch } from 'vue-property-decorator';
import { ROLES_VALUES } from '@/tools/values/staff.values';
import { TOAST_TYPES } from '@/plugins/ed-toast/values/ed-toast.values';
import { STAFF_STORE } from '@/tools/store/staff.store';
import EdEditStaffComponent from '../ed-edit-staff/ed-edit-staff.component.vue'
import EdDeleteStaffComponent from '../ed-delete-staff/ed-delete-staff.component.vue';
import { APP_EVENT_BUS } from '@/core/enums/app.enum';
import EdInputModel from '@/models/ed-input.model';

@Component({
  name: 'ed-list-class-staff',
  components: {
    EdEditStaffComponent,
    EdDeleteStaffComponent
  }
})
export default class EdListClassStaffComponent extends Vue {

  /* Props */
  @Prop() readonly orgId: number;
  
  /* Refs */
  @Ref('dataTable') readonly dataTable: any;
  
  /* Data */
  public search= EdInputModel('search',  '');
  public loading: boolean = true
  public roleFilter: string = ''
  public rolesList = ROLES_VALUES
  public unregistered: boolean = false
  public filteredUsers: any = []
  public usersSelected: any = []
  public filterFlag: boolean = false
  public pagination: object = { current: 1, total: 0 }
  public orgPagination: object = { current: 1, total: 0 }
  public filterOrgPagination: object = { current: 1, total: 0 }
  public filterUnregPagination: object = { current: 1, total: 0 }
  public showPaginator: boolean = true;
  
  /* Computed */
  get  headers() { 
    return [
      { text:  this.$t('classes.headers.username'), align: 'start', value: 'username' },
      { text: this.$t('classes.headers.name'), value: 'firstName' },
      { text: this.$t('classes.headers.lastName'), value: 'lastName' },
      { text: this.$t('classes.headers.email'), value: 'email'  },
      { text: this.$t('tools.staff.headers.role'), value: 'role'  },
      { text: this.$t('tools.staff.headers.status'), value: 'status'  },
      { text: this.$t('classes.headers.actions'), value: 'actions', sortable: false }
    ]
  }

  get organizationUsers() {
    return this.$store.state.StaffStore.orgUsers;
  }

  get unregisteredUsers() {
    return this.$store.state.StaffStore.unregUsers;
  }

  get filterOrganizationUsers() {
    return this.$store.state.StaffStore.filterOrgUsers;
  }

  get filterUnregisteredUsers() {
    return this.$store.state.StaffStore.filterUnregUsers;
  }

  get orgUsers() {
    this.loading = true;
    
     // Check with store if the page was loaded previously    
    let orgUsers = this.organizationUsers?.find(el => el.page === this.orgPagination['current']-1)?.content;
    
    // Fetch new page
    if (!orgUsers) {
      this.$store.dispatch(`${STAFF_STORE.ACTIONS.GET_ORGANIZATION_USERS_A}`,{
        orgId: this.orgId, 
        page: this.orgPagination['current']-1, 
        pageSize: 10
      }).then((res) => { 
        this.orgPagination['total'] = res.totalPages;
        this.loading = false;
        return res.content;
      }).catch(() => {
        this.loading = false;
      })
    } else this.loading = false;
    return orgUsers
  }

  get unregUsers() {
    this.loading = true;

     // Check with store if the page was loaded previously    
    let unregUsers = this.unregisteredUsers?.find(el => el.page === this.pagination['current']-1)?.content;

    // Fetch new page
    if (!unregUsers) {
      this.$store.dispatch(`${STAFF_STORE.ACTIONS.GET_UNREGISTERED_USERS_A}`,{
        page: this.pagination['current']-1, 
        pageSize: 10
      }).then((res) => { 
        this.pagination['total'] = res.totalPages;
        this.loading = false;
        return res.content;
      }).catch(() => {
        this.loading = false;
      })
    } else this.loading = false;
    return unregUsers
  }

  get items(): any[] {
    return !this.filterFlag ? (this.unregistered ? this.unregUsers : this.orgUsers) : this.filteredUsers;
  }

  get paginationObject(): any {

    if (this.unregistered) {
      return this.filterFlag ? this.filterUnregPagination : this.pagination;
    }

    return this.filterFlag ? this.filterOrgPagination : this.orgPagination;
  }

  get paginationPage(): number {
    return this.paginationObject ? this.paginationObject['current'] : 1;
  }

  get paginationLength(): number {
    return this.paginationObject ? this.paginationObject['total'] : 0;
  }

  /* Methods */
  deleteItem (item: Object) {
    this.$EdModal.createModal(EdDeleteStaffComponent, { 
      personId: item['personId'],
      orgId : this.orgId,
      userName: item['firstName'],
      isTeacher: item['type'] !== 'STUDENT',  
      action: async () => { await this.deleteItemConfirm(item); }  
    });
  }

  deleteItemConfirm (item) {
    this.$store.dispatch(`${STAFF_STORE.ACTIONS.REMOVE_USER_FROM_ORGANIZATION_A}`, 
    { id: item.id }
    ).then(() => {
      this.$EdToast.create({ 
        msg:  `${this.$t('tools.staff.success.userRemoved')}`,
        type: TOAST_TYPES.SUCCESS,
        top: '3rem',
        timeout: 3000
      });
    }).catch(() => {
      this.$EdToast.create({ 
        msg:  `${this.$t('tools.staff.errors.userNoRemoved')}`,
        type: TOAST_TYPES.DANGER,
        top: '3rem',
        timeout: 3000
      });
    })
  }

  inviteUser(user: any){
    if (user.status == 'PENDING') {
      this.$EdModal.createMessageModal({
        title: this.$t('tools.staff.info.actionNotPermited'),
        body: this.$t('tools.staff.info.userNeedsApproval', {userName: user.firstName}),
        type: KEYS.EVENT_TYPES.DANGER
      });
    } else {
      this.$EdModal.createModal(EdEditStaffComponent, { users: [user], orgId: this.orgId});
    }
  }

  inviteSelected() {
    const allEqual = this.usersSelected.every((val, i, arr) => val.role === arr[0].role)
    const allApproved = this.usersSelected.every((val) => val.status === 'APPROVED')

    if (allEqual && allApproved) {
      this.$EdModal.createModal(EdEditStaffComponent, { users: this.usersSelected, orgId: this.orgId});
    } else {
      this.$EdModal.createMessageModal({
        title: this.$t('tools.staff.info.actionNotPermited'),
        body: this.$t('tools.staff.info.allUsersSameRole'),
        type: KEYS.EVENT_TYPES.DANGER
      });
    }
  }

  filterOrgUsers() {
    if(!this.roleFilter || this.roleFilter == '') this.roleFilter = 'SDA,SA,TEACHER,STUDENT'
    
    this.filterOrgPagination = {
      current: 1,
      total: 0
    }
    this.$store.dispatch(`${STAFF_STORE.ACTIONS.SEARCH_USERS_ORGANIZATION_A}`,{
      orgId: this.orgId, 
      keyword: this.search.value,
      userTypes: this.roleFilter, 
      page: this.filterOrgPagination['current']-1, 
      pageSize: 10
    }).then((res) => { 
      this.filterOrgPagination['total'] = res.totalPages;
      this.loading = false;
      this.filteredUsers = res.content;
      this.filterFlag = true
    }).catch(() => {
      this.loading = false;
    })
  }

  filterPaginateOrgUsers(){
    this.loading = true;
    
    // Check with store if the page was loaded previously    
    let filtered = this.filterOrganizationUsers.find(el => el.page === this.filterOrgPagination['current']-1)?.content;

    // Fetch new page
    if (!filtered) {
      this.$store.dispatch(`${STAFF_STORE.ACTIONS.SEARCH_USERS_ORGANIZATION_A}`,{
        orgId: this.orgId, 
        keyword: this.search.value,
        userTypes: this.roleFilter, 
        page: this.filterOrgPagination['current']-1, 
        pageSize: 10
      }).then((res) => { 
        this.filterOrgPagination['total'] = res.totalPages;
        this.loading = false;
        this.filteredUsers = res.content;
      }).catch(() => {
        this.loading = false;
      })
    } else {
      this.filteredUsers = filtered;
      this.loading = false;
    }
  }

  filterUnregUsers() {
    if(!this.roleFilter || this.roleFilter == '') this.roleFilter = 'SDA,SA,TEACHER,STUDENT'
    
    this.filterUnregPagination = {
      current: 1,
      total: 0
    }
    this.$store.dispatch(`${STAFF_STORE.ACTIONS.SEARCH_UNREGISTERED_USERS_A}`,{
      keyword: this.search.value,
      userType: this.roleFilter, 
      page: this.filterUnregPagination['current']-1, 
      pageSize: 10
    }).then((res) => { 
      this.filterUnregPagination['total'] = res.totalPages;
      this.loading = false;
      this.filteredUsers = res.content;
      this.filterFlag = true
    }).catch(() => {
      this.loading = false;
    })
  }

  filterPaginateUnregUsers(){
    this.loading = true;
    
    // Check with store if the page was loaded previously    
    let filtered = this.filterOrganizationUsers.find(el => el.page === this.filterUnregPagination['current']-1)?.content;

    // Fetch new page
    if (!filtered) {
      this.$store.dispatch(`${STAFF_STORE.ACTIONS.SEARCH_UNREGISTERED_USERS_A}`,{
        keyword: this.search.value,
        userType: this.roleFilter, 
        page: this.filterUnregPagination['current']-1, 
        pageSize: 10
      }).then((res) => { 
        this.filterUnregPagination['total'] = res.totalPages;
        this.loading = false;
        this.filteredUsers = res.content;
      }).catch(() => {
        this.loading = false;
      })
    }
  }

  clearFilter() {
    this.filterFlag = false;
    this.search.value = '';
    this.roleFilter = '';
    this.filterOrgPagination = {
      current: 1,
      total: 0
    }
    this.filterUnregPagination = {
      current: 1,
      total: 0
    }
  }

  translateAndConvert(word) {
    let lowerWord = word.toLowerCase();
    return this.$t(`tools.staff.status.${lowerWord}`)
  }

  onPageChange(customEvent: CustomEvent): void {
    const detail: { page: number } = customEvent.detail;
    this.paginationObject['current'] = detail.page;

    if(this.unregistered)
      this.filterPaginateUnregUsers();
    else
      this.filterPaginateOrgUsers(); 
  }

  onSelectedItems(customEvent: CustomEvent): void {
    const detail: { selectedItems: any[] } = customEvent.detail;
    if (detail.selectedItems?.length > 0) {
      this.usersSelected = detail.selectedItems.map(item => item.value.raw);
    } else {
      this.usersSelected = [];
    }
  }

  private _onLanguageChanged(lang: string): void {
    this.dataTable?.setAttribute('lang', lang);
    this._scheduleLoadDataTable();
  }

  private _scheduleLoadDataTable(): void {
    this.$nextTick(() => {
      this._loadDataTable();
    });
  }

  private _loadDataTable(): void {
    if (!this.dataTable || !this.dataTable.loadData) {
      return;
    }

    if (!this.items || this.items?.length == 0) {
      this.dataTable.loadData({ items: [], headers: this.headers });
      return;
    }

    const slots = {
      'item.username': {
        template: document.querySelector('#item\\.username').innerHTML,
        templateParams: [{ 
          key: 'item.username',
          value: (params) => params.item.username ?? '',
        }],
      },
      'item.status': {
        template: '{item.status}',
        templateParams: [{
          key: 'item.status',
          value: (params) => params.parent.translateAndConvert(params.item.status) ?? '',
        }],
      },
      'item.role': {
        template: '{item.role}',
        templateParams: [{
          key: 'item.role',
          value: (params) => params.parent.translateAndConvert(params.item.role) ?? '',
        }],
      },
      'item.actions': {
        template: document.querySelector('#item\\.actions').innerHTML,
        expressions: [
          {
            target: '.invite-user', attribute: 'style', booleanAttribute: false,
            expression: (params) => params.parent.unregistered,
            applied: 'display: inline;', fallback: 'display: none;'
          },
          {
            target: '.remove-user', attribute: 'style', booleanAttribute: false,
            expression: (params) => !params.parent.unregistered,
            applied: 'display: inline;', fallback: 'display: none;'
          },
        ],
        events: [
          { target: '.invite-user', handler: (params) => params.parent.inviteUser(params.item) },
          { target: '.remove-user', handler: (params) => params.parent.deleteItem(params.item) },
        ],
      },
      parent: this,
    };

    this.dataTable.loadData({
      items: this.items,
      totalItems: this.items.length,
      headers: this.headers,
      slots: slots,
    });
  }

  @Watch('items', { deep: true, immediate: true })
  private _watchItems(): void {
    this._scheduleLoadDataTable();
  }

  @Watch('paginationPage', { deep: true, immediate: true })
  private _watchPaginationPage(): void {
    this._scheduleLoadDataTable();
  }

  @Watch('paginationLength', { deep: true, immediate: true })
  private _watchPaginationLength(): void {
    // Refresh the pagination component from initial state
    if (this.paginationLength === 0) {
      this.showPaginator = false;
      this.$nextTick(() => {
        this.showPaginator = true;
      });
    }
  }

  mounted() {
    this.$edEventBus.$on(APP_EVENT_BUS.LANGUAGE_CHANGED, this._onLanguageChanged);
  }

  beforeDestroy() {
    this.$edEventBus.$off(APP_EVENT_BUS.LANGUAGE_CHANGED, this._onLanguageChanged);
  }
}